import { Box, Card, Dialog, Stack } from '@mui/material';
import { ReactElement, useState, useEffect, useCallback } from 'react';
import {
  CreateButton,
  Datagrid,
  EditButton,
  TextField,
  useTranslate,
  DeleteButton,
  FunctionField,
  BooleanField,
  useNotify,
  NumberField,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import {
  getThresholdTiers,
  createThresholdTier,
  updateThresholdTier,
  deleteThresholdTier,
  ThresholdTierType,
  ThresholdTier,
} from '../../../services/redpacketService';
import CreateThresholdTier from './Create';
import EditThresholdTier from './Edit';
import '../dataProvider';

const DepositTierList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [popup, setPopup] = useState<ReactElement>();
  const [data, setData] = useState<ThresholdTier[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getThresholdTiers(ThresholdTierType.DEPOSIT);
      setData(Array.isArray(result) ? result : []);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [notify,translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    if (window.confirm(translate('module.redpacket.confirmDeletion'))) {
      try {
         await deleteThresholdTier(id);
        notify(translate('module.redpacket.deleteSuccess'), { type: 'success' });
        fetchData();
      } catch (error: any) {
        notify(error.message || translate('module.redpacket.deleteFailed'), { type: 'error' });
      }
    }
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.depositTierConfig')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.depositTierConfig') },
          ]}
          action={
            <PermissionGuard resource="redpacket" action="create" fallback={<></>}>
              <CreateButton
                variant="contained"
                size="medium"
                onClick={(e) => {
                  e.preventDefault();
                  setPopup(
                    <CreateThresholdTier
                      tierType={ThresholdTierType.DEPOSIT}
                      onClose={() => {
                        setPopup(undefined);
                        fetchData();
                      }}
                    />
                  );
                }}
              />
            </PermissionGuard>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ overflow: 'auto' }}>
            <Datagrid rowClick={false} data={data} isLoading={loading} bulkActionButtons={false}>
              <NumberField source="thresholdAmount" label={translate('module.redpacket.thresholdAmount')} />
              <NumberField source="packetsEarned" label={translate('module.redpacket.packetsEarnedCol')} />
              {/*<NumberField source="sortOrder" label="SortOrder" />*/}
              <BooleanField source="isActive" label={translate('module.redpacket.isActiveCol')} />
              <TextField source="description" label={translate('module.redpacket.descriptionCol')} />
              <FunctionField
                label={translate('module.redpacket.operateion')}
                render={(record: ThresholdTier) => (
                  <Stack direction="row" spacing={1}>
                    <PermissionButton resource="redpacket" action="update">
                      <EditButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setPopup(
                            <EditThresholdTier
                              id={record.id}
                              record={record}
                              tierType={ThresholdTierType.DEPOSIT}
                              onClose={() => {
                                setPopup(undefined);
                                fetchData();
                              }}
                            />
                          );
                        }}
                      />
                    </PermissionButton>
                    <PermissionButton resource="redpacket" action="delete">
                      <DeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(record.id);
                        }}
                      />
                    </PermissionButton>
                  </Stack>
                )}
              />
            </Datagrid>
          </Box>
        </Card>

        {popup && (
          <Dialog open maxWidth="md" fullWidth>
            {popup}
          </Dialog>
        )}
      </DashboardContent>
    </PermissionGuard>
  );
};

export default DepositTierList;
